import {
  DroDataType, DroFieldValidation, DroFormField, DroPreviousValue, DroPreviousValues, DroReadingValidation
} from '../model/dro.model';

const NUMBER_PATTERN = /^-?(\d+(\.\d*)?|\.\d+)$/;

/** Field codes are upper case and limited to A–Z, 0–9 and underscore. */
export function normalizeDroFieldCode(input: string | null | undefined): string {
  return (input ?? '').toUpperCase().replace(/[^A-Z0-9_]/g, '');
}

export function formatDroNumber(value: number, decimalPlaces?: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimalPlaces ?? 0,
    maximumFractionDigits: decimalPlaces ?? 4
  });
}

/** A date-only value (a reading's business day) as a local date; new Date() would read it as UTC midnight. */
export function toDroLocalDate(date: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00` : date);
}

/** Formats a reading date as "12 Aug 2026"; date-only values are read as local dates, not UTC. */
export function formatDroDate(date: string): string {
  return toDroLocalDate(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Whether a service order still owes its DRO reading. Readings belong to the serial wherever they are recorded, so a
 * reading against the order satisfies it, and so does any reading on the serial today (e.g. a standalone one from
 * Sales) — the one-per-day rule would otherwise block the technician from recording one.
 */
export function isDroReadingOutstanding(serviceOrderReadingId: string | null, todayReadingId: string | null): boolean {
  return !serviceOrderReadingId && !todayReadingId;
}

/** Stored value as displayed in history and the reading popup, using the decimals it was captured with. */
export function formatDroValue(value: number | string | null, decimalPlaces: number): string {
  if (value === null || value === '') {
    return '—';
  }
  return typeof value === 'number' ? formatDroNumber(value, decimalPlaces) : value;
}

/** Signed gain since the previous reading, e.g. "+152.6"; empty when there is no earlier value. */
export function formatDroDifference(difference: number | null, decimalPlaces: number): string {
  if (difference === null) {
    return '';
  }
  return `${difference >= 0 ? '+' : '−'}${formatDroNumber(Math.abs(difference), decimalPlaces)}`;
}

/** Read-only summary shown in the setup fields grid and under each Preview input, e.g. "0 … 999,999 · whole". */
export function getDroConstraintSummary(field: DroFormField): string {
  if (field.dataType === DroDataType.text) {
    return field.maxLength ? `≤ ${field.maxLength} chars` : '';
  }
  const min = field.minimum !== null ? formatDroNumber(field.minimum) : '';
  const max = field.maximum !== null ? formatDroNumber(field.maximum) : '';
  const precision = field.decimalPlaces === 0 ? 'whole' : `${field.decimalPlaces} dp`;
  return `${min} … ${max} · ${precision}`;
}

/** Validates one captured value in the order the spec lists the rules; the first failing rule names the reason. */
export function validateDroValue(field: DroFormField, raw: string | null | undefined, previous?: DroPreviousValue): DroFieldValidation {
  const text = (raw ?? '').trim();

  if (!text) {
    return field.required ? invalid('Required') : VALID;
  }

  if (field.dataType === DroDataType.text) {
    return field.maxLength && text.length > field.maxLength ? invalid(`No more than ${field.maxLength} characters`) : VALID;
  }

  if (!NUMBER_PATTERN.test(text)) {
    return invalid('Numbers only');
  }

  const value = Number(text);
  if (value < 0 && !field.allowNegative) {
    return invalid('Negative values are not allowed');
  }

  const decimals = text.split('.')[1]?.length ?? 0;
  if (decimals > field.decimalPlaces) {
    return invalid(field.decimalPlaces === 0
      ? 'Whole numbers only'
      : `No more than ${field.decimalPlaces} decimal place${field.decimalPlaces === 1 ? '' : 's'}`);
  }

  if (field.minimum !== null && value < field.minimum) {
    return invalid(`Must be ${formatDroNumber(field.minimum)} or higher`);
  }
  if (field.maximum !== null && value > field.maximum) {
    return invalid(`Must be ${formatDroNumber(field.maximum)} or lower`);
  }

  if (field.cumulative && previous && value < previous.value) {
    return invalid(`Must be ${formatDroNumber(previous.value, field.decimalPlaces)} or higher — ${formatDroDate(previous.readingDate)}`);
  }

  return VALID;
}

/** Validates a whole capture card; fields are checked in form order so the banner can name the first failure. */
export function validateDroReading(
  fields: DroFormField[],
  values: Record<string, string | null | undefined>,
  previous: DroPreviousValues = {}
): DroReadingValidation {
  const errors: Record<string, string> = {};
  let firstFailing: DroReadingValidation['firstFailing'] = null;

  [...fields].sort((a, b) => a.position - b.position).forEach(field => {
    const result = validateDroValue(field, values[field.fieldCode], previous[field.fieldCode]);
    if (!result.valid && result.message) {
      errors[field.fieldCode] = result.message;
      firstFailing ??= { field, message: result.message };
    }
  });

  const failingCount = Object.keys(errors).length;
  return { valid: failingCount === 0, errors, failingCount, firstFailing };
}

/** Converts validated card input into the stored value: numbers for Number fields, trimmed text or null otherwise. */
export function toDroStoredValue(field: DroFormField, raw: string | null | undefined): number | string | null {
  const text = (raw ?? '').trim();
  if (!text) {
    return null;
  }
  return field.dataType === DroDataType.number ? Number(text) : text;
}

const VALID: DroFieldValidation = { valid: true, message: null };

function invalid(message: string): DroFieldValidation {
  return { valid: false, message };
}
